/*
  Handles collection filtering UI:
  - Tracks selected category, sort option, and price range in `filterData`.
  - Keeps number inputs and range sliders in sync for price.
  - Updates the counters in the header and Apply button (base count is 1 for category).
  - Builds and navigates to the collection URL on Apply.
  - Clears sort/price but preserves the selected category on Clear.
*/
document.addEventListener("DOMContentLoaded", () => {
  const filterData = {
    sort: null,
    category: null,
    price: null,
  };

  function updateFilterCounts() {
    let count = 1;
    if (filterData.sort) count += 1;
    if (filterData.price) {
      const hasMin =
        /filter\.v\.price\.gte=([^&]*)/.exec(filterData.price)?.[1] ?? "";
      const hasMax =
        /filter\.v\.price\.lte=([^&]*)/.exec(filterData.price)?.[1] ?? "";
      if (hasMin !== "" || hasMax !== "") count += 1;
    }
    const headerBadge = document.querySelector(".tw-filter-title .tw-count");
    const applyBadge = document.querySelector(".tw-count-inline");
    if (headerBadge) headerBadge.textContent = `(${count})`;
    if (applyBadge) applyBadge.textContent = `(${count})`;
  }

  function handleSortFilter() {
    const sortInputs = document.querySelectorAll(
      '#tw-sort-list input[type="checkbox"]'
    );
    sortInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (input.checked) {
          sortInputs.forEach((i) => {
            if (i !== input) i.checked = false;
          });
          filterData.sort = `${input.name}=${input.value}`;
        } else {
          filterData.sort = null;
        }
        updateFilterCounts();
      });
    });
  }

  // Handles category filter (ensures one category is always selected)
  function handleCategoryFilter() {
    const listEl = document.querySelector("#tw-categories-list");
    const categories = window.TW_CATEGORIES?.list || [];
    const defaultHandle =
      window.TW_CATEGORIES?.selected || categories[0]?.handle || "";
    if (!listEl || !categories.length) return;

    listEl.innerHTML = categories
      .map(
        (c) => `
      <li class="filter-item filter-item--type-checkbox">
        <input id="tw-cat-${c.handle}" type="checkbox" name="tw_category" value="${c.handle}">
        <label for="tw-cat-${c.handle}" class="filter-item-label"><span class="label-text">${c.title}</span></label>
      </li>`
      )
      .join("");

    const inputs = listEl.querySelectorAll('input[name="tw_category"]');
    const def = listEl.querySelector(`input[value="${defaultHandle}"]`);
    if (def) def.checked = true;
    filterData.category = def
      ? `tw_category=${def.value}`
      : categories[0]
      ? `tw_category=${categories[0].handle}`
      : null;

    inputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        if (input.checked) {
          inputs.forEach((i) => {
            if (i !== input) i.checked = false;
          });
          filterData.category = `tw_category=${input.value}`;
        } else {
          event.target.checked = true;
        }
        updateFilterCounts();
      });
    });
  }

  function handlePriceFilter() {
    const minNum = document.getElementById("filter--price-__min-input");
    const maxNum = document.getElementById("filter--price-__max-input");
    const minSlider = document.getElementById(
      "filter--price-__min-slider-input"
    );
    const maxSlider = document.getElementById(
      "filter--price-__to-slider-input"
    );
    if (!minNum || !maxNum || !minSlider || !maxSlider) return;

    const sanitize = (v) => {
      const s = String(v ?? "")
        .trim()
        .replace(",", ".");
      if (s === "") return "";
      const n = parseFloat(s);
      return Number.isFinite(n) && n >= 0 ? String(n) : "";
    };

    function updatePriceFilter() {
      const min = sanitize(minNum.value);
      const max = sanitize(maxNum.value);
      if (min !== "") minSlider.value = min;
      if (max !== "") maxSlider.value = max;
      const gte = `filter.v.price.gte=${min}`;
      const lte = `filter.v.price.lte=${max}`;
      filterData.price = `${gte}&${lte}`;
      updateFilterCounts();
    }

    minNum.addEventListener("input", updatePriceFilter);
    maxNum.addEventListener("input", updatePriceFilter);
    minNum.addEventListener("change", updatePriceFilter);
    maxNum.addEventListener("change", updatePriceFilter);

    minSlider.addEventListener("input", () => {
      minNum.value = minSlider.value;
      updatePriceFilter();
    });
    maxSlider.addEventListener("input", () => {
      maxNum.value = maxSlider.value;
      updatePriceFilter();
    });

    minNum.value = sanitize(minNum.value);
    maxNum.value = sanitize(maxNum.value);
    minSlider.value = minNum.value || minSlider.min || 0;
    maxSlider.value = maxNum.value || maxSlider.max || 0;
    updatePriceFilter();
  }

  function handleFilterForm() {
    const filterForm = document.getElementById("tw-filter-form");
    const basePath = "/collections/";
    if (!filterForm) return;

    filterForm.addEventListener("submit", (event) => {
      event.preventDefault();
      const parts = (filterData.category || "").split("=");
      const collectionHandle = parts[1] || "all";

      let newUrl = basePath + collectionHandle;
      const q = [];
      if (filterData.sort) q.push(filterData.sort);
      if (filterData.price) {
        filterData.price.split("&").forEach((s) => {
          if (s) q.push(s);
        });
      }
      if (q.length) newUrl += "?" + q.join("&");
      window.location.href = newUrl;
    });

    const clearButton = document.getElementById("tw-clear");
    if (clearButton) {
      clearButton.addEventListener("click", () => {
        document
          .querySelectorAll('#tw-sort-list input[type="checkbox"]')
          .forEach((i) => (i.checked = false));
        filterData.sort = null;

        const minNum = document.getElementById("filter--price-__min-input");
        const maxNum = document.getElementById("filter--price-__max-input");
        const minSlider = document.getElementById(
          "filter--price-__min-slider-input"
        );
        const maxSlider = document.getElementById(
          "filter--price-__to-slider-input"
        );
        if (minNum) minNum.value = "";
        if (maxNum) maxNum.value = "";
        if (minSlider) minSlider.value = minSlider.min || 0;
        if (maxSlider) maxSlider.value = maxSlider.max || 0;
        filterData.price = "filter.v.price.gte=&filter.v.price.lte=";

        let selected = document.querySelector(
          '#tw-categories-list input[name="tw_category"]:checked'
        );
        if (!selected) {
          const defHandle =
            window.TW_CATEGORIES?.selected ||
            window.TW_CATEGORIES?.list?.[0]?.handle ||
            "all";
          selected = document.querySelector(
            `#tw-categories-list input[value="${defHandle}"]`
          );
          if (selected) selected.checked = true;
          filterData.category = `tw_category=${defHandle}`;
        } else {
          filterData.category = `tw_category=${selected.value}`;
        }
        updateFilterCounts();
      });
    }
  }

  handleFilterForm();
  handleCategoryFilter();
  handlePriceFilter();
  handleSortFilter();
});
